import React, { useMemo } from 'react';
import { Row, Table, Typography } from 'antd';
import SealApprovalAttachmentsView from '../Admin/SealApprovalAttachmentsView';
import {
  FINANCE_READONLY_GUTTER,
  FinanceReadonlyItem,
  formatFinanceAmount
} from './financeReadonlyUtils';
import './ExpenseClaimForm.css';

const { Text } = Typography;

function parsePaymentLines(formData = {}) {
  let lines = formData.payment_lines;
  if (typeof lines === 'string') {
    try {
      lines = JSON.parse(lines);
    } catch {
      lines = [];
    }
  }
  return Array.isArray(lines) ? lines : [];
}

function parseAttachments(formData = {}) {
  let attachments = formData.attachments;
  if (typeof attachments === 'string') {
    try {
      attachments = JSON.parse(attachments);
    } catch {
      attachments = [];
    }
  }
  return Array.isArray(attachments) ? attachments : [];
}

function resolveFormPeriod(formData = {}) {
  if (formData.period) return formData.period;
  const lines = parsePaymentLines(formData);
  return lines.find((line) => line?.period)?.period || '—';
}

function buildSocialFundPaymentLineRows(formData = {}) {
  return parsePaymentLines(formData).map((line, index) => ({
    key: String(index),
    lineNo: index + 1,
    company_name: line.company_name || line.company_id || '—',
    headcount: line.headcount ?? '—',
    payable_amount: line.payable_amount,
    paid_amount: line.paid_amount,
    diff_amount: line.diff_amount,
    diff_remark: line.diff_remark || '—'
  }));
}

const LINE_COLUMNS = [
  { title: '序号', dataIndex: 'lineNo', width: 56, fixed: 'left' },
  { title: '公司主体', dataIndex: 'company_name', width: 180, ellipsis: true },
  { title: '缴费人数', dataIndex: 'headcount', width: 88, align: 'right' },
  {
    title: '应缴费额',
    dataIndex: 'payable_amount',
    width: 108,
    align: 'right',
    render: (value) => formatFinanceAmount(value)
  },
  {
    title: '实缴费额',
    dataIndex: 'paid_amount',
    width: 108,
    align: 'right',
    render: (value) => formatFinanceAmount(value)
  },
  {
    title: '差额',
    dataIndex: 'diff_amount',
    width: 96,
    align: 'right',
    render: (value) => formatFinanceAmount(value)
  },
  { title: '差额备注', dataIndex: 'diff_remark', width: 160, ellipsis: true }
];

export default function SocialFundPaymentReadonlyView({ formData = {} }) {
  const lines = useMemo(() => buildSocialFundPaymentLineRows(formData), [formData]);
  const attachments = useMemo(() => parseAttachments(formData), [formData]);
  const period = useMemo(() => resolveFormPeriod(formData), [formData]);

  return (
    <div className="process-form-finance-grouped social-fund-payment-readonly">
      <section className="expense-claim-section">
        <div className="expense-claim-section-title">基本信息</div>
        <Row gutter={FINANCE_READONLY_GUTTER} className="prepayment-readonly-grid">
          <FinanceReadonlyItem label="类型" value={formData.payment_type_label || '—'} />
          <FinanceReadonlyItem label="费用期间" value={period} />
        </Row>
      </section>

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">缴费明细</div>
        {lines.length > 0 ? (
          <Table
            rowKey="key"
            size="small"
            columns={LINE_COLUMNS}
            dataSource={lines}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 920 }}
          />
        ) : (
          <div className="prepayment-readonly-value" style={{ whiteSpace: 'pre-wrap' }}>
            {formData.payment_lines_summary || '—'}
          </div>
        )}
      </section>

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">合计</div>
        <Row gutter={FINANCE_READONLY_GUTTER} className="prepayment-readonly-grid">
          <FinanceReadonlyItem label="缴费人数合计" value={formData.total_headcount ?? '—'} />
          <FinanceReadonlyItem label="应缴费额合计" value={formatFinanceAmount(formData.total_payable_amount)} />
          <FinanceReadonlyItem label="实缴金额合计" value={formatFinanceAmount(formData.total_paid_amount)} />
          <FinanceReadonlyItem label="抵扣额/差额合计" value={formatFinanceAmount(formData.total_diff_amount)} />
        </Row>
      </section>

      {(attachments.length > 0 || formData.remark) && (
        <section className="expense-claim-section">
          <div className="expense-claim-section-title">附件与备注</div>
          {attachments.length > 0 && (
            <div className="process-form-snapshot-attachments" style={{ marginBottom: 12 }}>
              <SealApprovalAttachmentsView attachments={attachments} title="附件" />
            </div>
          )}
          {formData.remark && (
            <div className="prepayment-readonly-item">
              <Text type="secondary" className="prepayment-readonly-label">备注</Text>
              <div className="prepayment-readonly-value">{formData.remark}</div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
